// The client store holds what the server last said plus a little local UI state. No game rules
// live here: the view is replaced wholesale by every state message.
import { create } from "zustand";
import type { GameEvent, PlayerView, RoleCard, StateMsg, WelcomeMsg } from "../protocol";
import { loadPrefs, savePrefs, type Prefs } from "./local";

export type SessionStatus =
  | "idle" // room page before anything has happened
  | "need_name" // no seat on this device: ask for a name
  | "connecting" // socket opening / handshake in flight
  | "live" // seated and receiving state
  | "reconnecting" // lost the socket after being seated
  | "not_found"
  | "game_in_progress"
  | "room_full"
  | "superseded" // the same seat token opened on another device or tab
  | "moved" // a rejoin code moved this seat to another phone: this phone's token is dead
  | "left"
  | "kicked"
  | "closed";

export interface UiState {
  /** Highest day number whose dawn announcement this phone has dismissed. */
  dawnSeenDay: number;
  notebookOpen: boolean;
  /** Seat whose action sheet is open on the day screen. */
  sheetSeatId: string | null;
}

/** Which form the join page opens on: a name, or a rejoin code from the host. */
export type JoinMode = "name" | "reseat";

export interface Toast {
  id: number;
  text: string;
  tone: "info" | "error";
}

export interface GameStore {
  code: string | null;
  status: SessionStatus;
  seatId: string | null;
  seq: number;
  view: PlayerView | null;
  roles: Record<string, RoleCard>;
  /** The server's configured public address (TC_PUBLIC_URL), for share links. */
  publicUrl: string | null;
  recentEvents: GameEvent[];
  /** Increments whenever events arrive, so effects can react to new ones. */
  eventTick: number;
  offsetMs: number;
  joinError: string | null;
  joinMode: JoinMode;
  toast: Toast | null;
  ui: UiState;
  prefs: Prefs;

  reset(code: string): void;
  setStatus(status: SessionStatus): void;
  applyWelcome(msg: WelcomeMsg): void;
  applyState(msg: StateMsg): boolean;
  setOffset(ms: number): void;
  setJoinError(message: string | null): void;
  setJoinMode(mode: JoinMode): void;
  showToast(text: string, tone?: Toast["tone"]): void;
  clearToast(id: number): void;
  setUi(patch: Partial<UiState>): void;
  setPrefs(patch: Partial<Prefs>): void;
}

const INITIAL_UI: UiState = { dawnSeenDay: 0, notebookOpen: false, sheetSeatId: null };
const MAX_RECENT_EVENTS = 50;
let toastCounter = 0;

export const useGame = create<GameStore>((set, get) => ({
  code: null,
  status: "idle",
  seatId: null,
  seq: 0,
  view: null,
  roles: {},
  publicUrl: null,
  recentEvents: [],
  eventTick: 0,
  offsetMs: 0,
  joinError: null,
  joinMode: "name",
  toast: null,
  ui: INITIAL_UI,
  prefs: loadPrefs(),

  reset(code) {
    set({
      code,
      status: "idle",
      seatId: null,
      seq: 0,
      view: null,
      recentEvents: [],
      joinError: null,
      joinMode: "name",
      toast: null,
      ui: INITIAL_UI,
    });
  },

  setStatus(status) {
    set({ status });
  },

  applyWelcome(msg) {
    const roles: Record<string, RoleCard> = {};
    for (const r of msg.roles) roles[r.id] = r;
    // a welcome starts a fresh sequence for this connection's seat
    set({ seatId: msg.seat_id, roles, seq: 0, joinError: null, publicUrl: msg.public_url });
    get().applyState(msg.state);
  },

  applyState(msg) {
    const state = get();
    if (msg.seq <= state.seq) return false; // stale or duplicate
    const roles = { ...state.roles };
    for (const r of msg.view.roles) roles[r.id] = r;
    set({
      seq: msg.seq,
      view: msg.view,
      roles,
      recentEvents: msg.events.length
        ? [...state.recentEvents, ...msg.events].slice(-MAX_RECENT_EVENTS)
        : state.recentEvents,
      eventTick: state.eventTick + (msg.events.length ? 1 : 0),
    });
    return true;
  },

  setOffset(ms) {
    set({ offsetMs: ms });
  },

  setJoinError(message) {
    set({ joinError: message });
  },

  setJoinMode(mode) {
    set({ joinMode: mode });
  },

  showToast(text, tone = "info") {
    toastCounter += 1;
    set({ toast: { id: toastCounter, text, tone } });
  },

  clearToast(id) {
    if (get().toast?.id === id) set({ toast: null });
  },

  setUi(patch) {
    set({ ui: { ...get().ui, ...patch } });
  },

  setPrefs(patch) {
    const prefs = { ...get().prefs, ...patch };
    savePrefs(prefs);
    set({ prefs });
  },
}));
